import React, { useState } from "react";
import Flatpickr from "react-flatpickr";
import { SERIES_FLATPICKR, tagSeriesCalendar } from "../seriesFlatpickr";
import { Badge, Button, Tabs, Edit01, Trash04 } from "@opendate-app/setlist";
import { useSeriesEditContext } from "../SeriesEditApp";
import {
  offsetDateForBrowser,
  offsetDateForServer,
} from "../../../shared/timeZoneLogic";
import {
  activityRows,
  describeTiming,
  timingOf,
  writeTiming,
} from "../seriesActivities";
import ShowDetailOffset, { offsetsWithinAYear } from "./ShowDetailOffset";

const MODES = [
  { value: "each", label: "Each date" },
  { value: "same", label: "Same for all" },
];

const DEFAULT_OFFSET_MINUTES = 7 * 24 * 60;

const SeriesActivitiesCard = () => {
  const { form, setField, extraShowDetailNames = [] } = useSeriesEditContext();
  const timeZone = form.venueTimeZone;
  const [editingName, setEditingName] = useState(null);
  const [draft, setDraft] = useState(null);

  const rows = activityRows(form, extraShowDetailNames);
  const unsetCount = rows.filter((row) => row.column && !timingOf(form, row)).length;

  const beginEdit = (row) => {
    const timing = timingOf(form, row);
    const each = timing ? timing.offset !== undefined : !!form.isDraft;
    setDraft({
      mode: each ? "each" : "same",
      offset:
        timing && timing.offset !== undefined
          ? timing.offset
          : DEFAULT_OFFSET_MINUTES,
      date:
        timing && timing.at ? offsetDateForBrowser(timing.at, timeZone) : null,
    });
    setEditingName(row.name);
  };

  const closeEdit = () => {
    setEditingName(null);
    setDraft(null);
  };

  const saveEdit = (row) => {
    if (draft.mode === "each") {
      writeTiming(form, setField, row, { offset: draft.offset });
    } else {
      writeTiming(
        form,
        setField,
        row,
        draft.date
          ? { at: offsetDateForServer(draft.date, timeZone).toISOString() }
          : null,
      );
    }
    closeEdit();
  };

  const canSave =
    draft &&
    (draft.mode === "each" ? offsetsWithinAYear(draft.offset) : !!draft.date);

  return (
    <section className="series-step-panel series-activities-panel">
      <div className="series-step-panel-header">
        <div>
          <h3 className="series-step-panel-title">Show Activities</h3>
          <span className="series-step-panel-hint">
            When each date is announced and goes on sale. Set them once,
            relative to each date, and every date gets its own times. Ticket
            windows, promo codes and emails can start from any of them.
          </span>
        </div>
        {unsetCount > 0 && (
          <Badge color="warning" size="sm">
            {unsetCount} not set
          </Badge>
        )}
      </div>
      <div className="series-step-panel-body">
        <table className="series-activities-table">
          <tbody>
            {rows.map((row) => {
              const timing = timingOf(form, row);
              if (editingName === row.name && draft) {
                return (
                  <tr key={row.name} className="series-activities-editing">
                    <td className="series-activities-name">{row.name}</td>
                    <td className="series-activities-time">
                      <div className="series-activities-editor">
                        {form.isDraft && (
                          <Tabs
                            variant="button-border"
                            size="sm"
                            collapseBelow={0}
                            aria-label={`${row.name} timing`}
                            items={MODES}
                            value={draft.mode}
                            onChange={(mode) =>
                              setDraft((current) => ({ ...current, mode }))
                            }
                          />
                        )}
                        {draft.mode === "each" ? (
                          <ShowDetailOffset
                            label={row.name}
                            anchorName="each date's start"
                            minutes={draft.offset}
                            onChange={(offset) =>
                              setDraft((current) => ({ ...current, offset }))
                            }
                          />
                        ) : (
                          <Flatpickr
                            className="series-edit-input"
                            options={{
                              ...SERIES_FLATPICKR,
                              enableTime: true,
                              dateFormat: "M j, Y h:i K",
                              allowInput: true,
                            }}
                            value={draft.date || ""}
                            placeholder="Select date and time"
                            aria-label={row.name}
                            onChange={([date]) =>
                              setDraft((current) => ({
                                ...current,
                                date: date || null,
                              }))
                            }
                            onReady={tagSeriesCalendar}
                          />
                        )}
                      </div>
                    </td>
                    <td className="series-activities-actions">
                      <span className="series-activities-edit-actions">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={closeEdit}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          disabled={!canSave}
                          onClick={() => saveEdit(row)}
                        >
                          Save
                        </Button>
                      </span>
                    </td>
                  </tr>
                );
              }
              const editable =
                form.isDraft || !(timing && timing.offset !== undefined);
              return (
                <tr key={row.name}>
                  <td className="series-activities-name">{row.name}</td>
                  <td className="series-activities-time">
                    {timing ? (
                      <span className="series-activities-value">
                        {describeTiming(timing, timeZone)}
                      </span>
                    ) : (
                      <span className="series-activities-unset">
                        <Badge color={row.column ? "warning" : "gray"} size="sm">
                          Not set
                        </Badge>
                        <Button
                          variant="link-color"
                          size="sm"
                          onClick={() => beginEdit(row)}
                        >
                          Set time
                        </Button>
                      </span>
                    )}
                  </td>
                  <td className="series-activities-actions">
                    {editable && (
                      <span className="series-row-actions">
                        <Button
                          variant="tertiary"
                          size="sm"
                          icon={Edit01}
                          aria-label={`Edit ${row.name}`}
                          onClick={() => beginEdit(row)}
                        />
                        {timing && (
                          <Button
                            variant="tertiary"
                            size="sm"
                            icon={Trash04}
                            className="series-row-action-destructive"
                            aria-label={`Clear ${row.name}`}
                            onClick={() =>
                              writeTiming(form, setField, row, null)
                            }
                          />
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default SeriesActivitiesCard;
